import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { brand } from '../../config/brand';
import { api } from '../../lib/api';
import { useFormErrors } from '../auth/useFormErrors';
import { Textarea } from '../../components/ui/form';

const FIELD =
  'rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong';

/**
 * Filing a copyright claim (HANDOFF §11).
 *
 * ## Public, and it has to be
 *
 * A rightsholder is usually a label, an agent or somebody who has never heard
 * of this site. Putting this behind a sign-up would mean the only people who
 * can report infringement are the people already using the service, which is
 * both backwards and a choice not to hear about it.
 *
 * ## It says what it will and will not do
 *
 * Filing takes nothing down. A form anybody can post to must never be able to
 * silence a track, or the takedown process becomes the censorship tool - so the
 * page says a person reviews it, and says that the uploader will be told and
 * can answer. Somebody filing in bad faith should know that before they sign.
 */
export function DmcaPage() {
  const errors = useFormErrors();
  const [reference, setReference] = useState<string | null>(null);

  const [form, setForm] = useState({
    claimant_name: '',
    claimant_email: '',
    claimant_org: '',
    track_id: '',
    work_description: '',
    signature: '',
  });
  const [sworn, setSworn] = useState(false);
  const [goodFaith, setGoodFaith] = useState(false);

  const file = useMutation({
    mutationFn: () =>
      api.post<{ id: number; reference: string }>('/dmca/notices', {
        ...form,
        track_id: Number(form.track_id),
        sworn_statement: sworn,
        good_faith: goodFaith,
      }),
    onError: errors.capture,
    onSuccess: (data) => setReference(data.reference),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    errors.clear();
    file.mutate();
  };

  if (reference) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-14">
        <Card className="flex flex-col gap-4 p-8">
          <h1 className="text-2xl font-bold tracking-tight text-ink">Notice received</h1>
          <p className="text-[15px] leading-relaxed text-soft">
            Your reference is{' '}
            <span className="font-mono text-ink" data-dmca-reference>
              {reference}
            </span>
            . Quote it in any correspondence about this claim.
          </p>
          <p className="text-sm leading-relaxed text-soft">
            A person will review it. If it is acted on, the recording is taken down and the
            uploader is told — they have the right to file a counter-notice, and you will be sent
            it if they do.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-14">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Report a copyright claim</h1>

      <p className="mt-4 text-[15px] leading-relaxed text-soft">
        If you own a recording that has been uploaded to {brand.name} without your permission, tell
        us here. This form is the fastest route — it goes straight into the queue a person reviews.
      </p>

      {/*
        Said before the form rather than after it. Somebody about to swear a
        statement should know what it triggers, and somebody filing to silence a
        critic should know it will be read by a person and answered.
      */}
      <Card className="mt-6 flex flex-col gap-2 p-5">
        <span className="meta-caps text-faint">What happens next</span>
        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm leading-relaxed text-soft">
          <li>Nothing comes down automatically. A person reviews every claim.</li>
          <li>If it is upheld, the recording is taken down and the uploader is told why.</li>
          <li>They can file a counter-notice, which we will forward to you.</li>
          <li>Knowingly false claims carry liability. The two statements below are not a formality.</li>
        </ul>
      </Card>

      <form onSubmit={submit} className="mt-8 flex flex-col gap-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Your name</span>
            <input
              required
              value={form.claimant_name}
              maxLength={160}
              data-dmca-name
              onChange={(e) => setForm({ ...form, claimant_name: e.target.value })}
              className={FIELD}
            />
            {errors.fields.claimant_name && (
              <span className="text-xs text-negative">{errors.fields.claimant_name}</span>
            )}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Email</span>
            <input
              required
              type="email"
              value={form.claimant_email}
              maxLength={200}
              data-dmca-email
              onChange={(e) => setForm({ ...form, claimant_email: e.target.value })}
              className={FIELD}
            />
            {errors.fields.claimant_email && (
              <span className="text-xs text-negative">{errors.fields.claimant_email}</span>
            )}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Company or label (optional)</span>
            <input
              value={form.claimant_org}
              maxLength={160}
              onChange={(e) => setForm({ ...form, claimant_org: e.target.value })}
              className={FIELD}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Track ID</span>
            <input
              required
              inputMode="numeric"
              value={form.track_id}
              data-dmca-track
              placeholder="the number in the track's address"
              onChange={(e) => setForm({ ...form, track_id: e.target.value.replace(/\D/g, '') })}
              className={FIELD}
            />
            {/* The URL is the only identifier a claimant has, so the field is
                explained in terms of where they found it. */}
            <span className="text-xs text-faint">
              From the address bar: {brand.domain}/tracks/<span className="text-soft">123</span>
            </span>
            {errors.fields.track_id && (
              <span className="text-xs text-negative">{errors.fields.track_id}</span>
            )}
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-soft">The work you own</span>
          <Textarea
            required
            value={form.work_description}
            maxLength={5000}
            data-dmca-work
            placeholder="Title, artist, catalogue number, release date, and where the original can be heard."
            onChange={(e) => setForm({ ...form, work_description: e.target.value })}
            className={`${FIELD} min-h-32 resize-y`}
          />
          {errors.fields.work_description && (
            <span className="text-xs text-negative">{errors.fields.work_description}</span>
          )}
        </label>

        <Affirm checked={goodFaith} onChange={setGoodFaith} error={errors.fields.good_faith}>
          I believe in good faith that this use is not authorised by the rightsholder, its agent,
          or the law.
        </Affirm>

        <Affirm checked={sworn} onChange={setSworn} error={errors.fields.sworn_statement}>
          I swear that the information here is accurate and that I am the rightsholder or
          authorised to act on their behalf. I understand a knowingly false claim carries
          liability.
        </Affirm>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-soft">Signature</span>
          <input
            required
            value={form.signature}
            maxLength={160}
            data-dmca-signature
            placeholder="Your full legal name"
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
            className={FIELD}
          />
        </label>

        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <div className="flex items-center gap-4">
          <Button type="submit" loading={file.isPending} data-dmca-submit>
            File the notice
          </Button>
          <Link to="/" className="text-sm text-soft hover:text-ink">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

/**
 * One of the two statements that make a notice a notice.
 *
 * A real checkbox rather than a styled div: this is a legal affirmation, and it
 * should behave exactly like every other checkbox the person has ever ticked -
 * space to toggle, label to click, announced as checked.
 */
function Affirm({
  checked,
  onChange,
  error,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border border-line p-3.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-signal)]"
        />
        <span className="text-[13px] leading-relaxed text-soft">{children}</span>
      </label>
      {error && <span className="text-xs text-negative">{error}</span>}
    </div>
  );
}
